import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchLpPositions, refreshLpPositions } from '../../api/lp'
import { useAuth } from '../../auth/AuthContext'
import { useShimmerOnUpdate } from '../../hooks/useShimmerOnUpdate'
import type { LpState } from '../../types/lp'
import type { StageAllocation } from '../../types/strategy'
import TokenIcon from '../common/TokenIcon'
import LpManagePanel from './LpManagePanel'

function pct(v: string, decimals = 2) {
  return (parseFloat(v) * 100).toFixed(decimals) + '%'
}
function usd(v: string) {
  return '$' + parseFloat(v).toLocaleString('en-US', { maximumFractionDigits: 2 })
}
function num(v: string, d = 4) {
  return parseFloat(v).toFixed(d)
}

// Grace period: treat empty positions as still-loading for 30s after mount/login
// (backend needs time to poll Raydium and push through the Kafka pipeline)
const GRACE_MS = 30_000

export default function LpPanel({ onPanelUsd, wsData, lpStage }: { onPanelUsd?: (key: 'lp' | 'claimable', usd: number) => void; wsData?: LpState[] | null; lpStage?: StageAllocation | null } = {}) {
  const { portfolioId } = useAuth()
  const [positions, setPositions] = useState<LpState[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [manageCtx, setManageCtx] = useState<{ positionMint?: string; provider?: 'raydium' | 'orca' } | null>(null)
  const shimmerRef = useShimmerOnUpdate(wsData)

  // Initial data: use batch snapshot from parent when available, otherwise fall back to
  // individual REST fetch. wsData intentionally excluded from deps.
  useEffect(() => {
    if (!portfolioId) return
    if (wsData != null) { setLoading(false); return }
    setLoading(true)
    fetchLpPositions(portfolioId)
      .then(data => { setPositions(data); setError(null) })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false))
  }, [portfolioId])  

  // Live update from WebSocket push
  useEffect(() => {
    if (wsData == null) return
    setPositions(wsData)
    setLoading(false)
  }, [wsData])

  // Called after the user submits an add/remove/close LP transaction (see LpManagePanel) to
  // pick up the on-chain change. Uses the /refresh endpoint — which forces a fresh Raydium/Orca
  // on-chain scan — rather than fetchLpPositions' plain cache read, which would otherwise keep
  // showing pre-transaction data for up to ~100s (the periodic lazy-probe interval).
  const refreshPositions = useCallback(() => {
    if (!portfolioId) return
    refreshLpPositions(portfolioId).then(data => setPositions(data)).catch(() => {})
  }, [portfolioId])

  const [grace, setGrace] = useState(true)
  const graceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    setGrace(true)
    graceTimer.current = setTimeout(() => setGrace(false), GRACE_MS)
    return () => { if (graceTimer.current) clearTimeout(graceTimer.current) }
  }, [portfolioId])

  // End grace period immediately once positions arrive
  useEffect(() => {
    if (positions && positions.length > 0 && grace) {
      if (graceTimer.current) clearTimeout(graceTimer.current)
      setGrace(false)
    }
  }, [positions])

  useEffect(() => {
    if (positions) {
      onPanelUsd?.('lp', positions.reduce((s, p) => s + parseFloat(p.lpValueUsd), 0))
      onPanelUsd?.('claimable', positions.reduce((s, p) => s + parseFloat(p.totalPendingYieldUsd), 0))
    }
  }, [positions])

  const totalValue = positions?.reduce((s, p) => s + parseFloat(p.lpValueUsd), 0) ?? 0
  const totalYield = positions?.reduce((s, p) => s + parseFloat(p.totalPendingYieldUsd), 0) ?? 0
  const totalIlUsd = positions?.reduce((s, p) => s + parseFloat(p.expectedIlUsd), 0) ?? 0

  return (
    <div ref={shimmerRef} className="bg-surface-card rounded-xl border border-fringe border-spectral font-sans overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-fringe">
        <div className="w-7 h-7 rounded-lg bg-cyan-400/10 flex items-center justify-center text-cyan-400">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4">
            <path d="M2 12c2-4 4-6 6-6s4 4 6 4 4-4 6-4"/>
            <path d="M2 17c2-3 4-5 6-5s4 3 6 3 4-3 6-3"/>
          </svg>
        </div>
        <span className="text-sm font-semibold text-ink">Concentrated LP</span>
        {positions && (
          <span className="ml-1 text-xs bg-accent-blue/20 text-accent-blue px-1.5 py-0.5 rounded-full font-medium">
            {positions.length}
          </span>
        )}
        {positions && positions.length > 0 && (
          <span className="text-xs text-red-400/80 bg-red-400/10 px-1.5 py-0.5 rounded-full font-medium">
            IL {usd(String(totalIlUsd))}
          </span>
        )}
        <div className="ml-auto flex items-center gap-3">
          {positions && positions.length > 0 && (
            <>
              <span className="text-sm font-semibold text-ink">{usd(String(totalValue))}</span>
              {totalYield > 0 && (
                <span className="text-xs text-gray-500">
                  yield <span className="text-accent-green font-medium">{usd(String(totalYield))}</span>
                </span>
              )}
            </>
          )}
          <button
            onClick={() => setManageCtx({})}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-cyan-400/20 hover:bg-cyan-400/40 text-cyan-400 hover:text-cyan-300 text-[11px] font-medium transition-colors"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
            </svg>
            Manage
          </button>
        </div>
      </div>

      {/* Body */}
      {error && (
        <p className="text-accent-red text-xs p-4">{error}</p>
      )}
      {(loading || (grace && (!positions || positions.length === 0))) && (
        <div className="animate-pulse p-4 space-y-2">
          <div className="h-3 w-full bg-surface rounded"/>
          <div className="h-3 w-3/4 bg-surface rounded"/>
        </div>
      )}
      {!loading && !grace && positions && positions.length === 0 && (
        <p className="text-gray-500 text-sm text-center py-8">No LP positions found</p>
      )}
      {!loading && positions && positions.length > 0 && (
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-surface">
          <div className="min-w-[640px]">
          {/* Column headers */}
          <div className="grid grid-cols-[1.8fr_0.7fr_2fr_1fr_1fr_1fr_1fr] gap-x-3 px-4 sm:px-5 py-2 text-xs text-gray-500 uppercase tracking-wider border-b border-fringe">
            <span>Pool</span>
            <span>Status</span>
            <span>Price Range</span>
            <span className="text-right">Size</span>
            <span className="text-right">Fee APR</span>
            <span className="text-right">Total APR</span>
            <span className="text-right">Fees Accrued</span>
          </div>

          <div className="max-h-[420px] overflow-y-auto scrollbar-thin scrollbar-thumb-surface scrollbar-track-transparent divide-y divide-white/5">
            {positions.map((pos, i) => (
              <PositionRow
                key={pos.pair + pos.provider + i}
                data={pos}
                onManage={() => setManageCtx({ positionMint: pos.positionMint, provider: pos.provider as 'raydium' | 'orca' })}
              />
            ))}
          </div>
          </div>
        </div>
      )}
      {manageCtx !== null && (
        <LpManagePanel
          positions={positions ?? []}
          lpStage={lpStage ?? undefined}
          onClose={() => setManageCtx(null)}
          prefillProvider={manageCtx.provider}
          prefillPositionMint={manageCtx.positionMint}
          onRefresh={refreshPositions}
        />
      )}
    </div>
  )
}

function ProviderBadge({ provider }: { provider: string }) {
  const key = (provider || '').toLowerCase()
  const label = key ? key.charAt(0).toUpperCase() + key.slice(1) : '—'
  const cls = key === 'raydium'
    ? 'bg-cyan-400/15 text-cyan-300 border border-cyan-400/30'
    : key === 'orca'
      ? 'bg-violet-400/15 text-violet-300 border border-violet-400/30'
      : 'bg-surface text-gray-400 border border-fringe'
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>
      {label}
    </span>
  )
}

function PositionRow({ data, onManage }: { data: LpState; onManage?: () => void }) {
  const [sym0, sym1] = data.pair ? data.pair.split('/') : ['—', '—']
  const priceLow  = parseFloat(data.priceLower)
  const priceHigh = parseFloat(data.priceUpper)
  const priceCur  = parseFloat(data.currentPrice)
  const rangeWidth = priceHigh - priceLow
  const cursorPct  = rangeWidth > 0
    ? Math.min(Math.max((priceCur - priceLow) / rangeWidth, 0), 1) * 100
    : 50

  return (
    <div className="grid grid-cols-[1.8fr_0.7fr_2fr_1fr_1fr_1fr_1fr] gap-x-3 px-4 sm:px-5 py-2.5 sm:py-3 items-start hover:bg-surface-hover/30 transition-colors text-sm">

      {/* Pool */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <div className="flex -space-x-1">
            <TokenIcon symbol={sym0} mint={data.token0Mint} size={18} />
            <TokenIcon symbol={sym1} mint={data.token1Mint} size={18} />
          </div>
          <span className="font-medium text-ink">{data.pair}</span>
          <ProviderBadge provider={data.provider} />
        </div>
        <div className="text-xs text-ink-muted space-y-0.5">
          <div className="flex items-center gap-1">
            <TokenIcon symbol={sym0} mint={data.token0Mint} size={13} />
            <span>{sym0}</span>
            <span className="text-ink-soft">{num(data.token0Amount, 3)}</span>
          </div>
          <div className="flex items-center gap-1">
            <TokenIcon symbol={sym1} mint={data.token1Mint} size={13} />
            <span>{sym1}</span>
            <span className="text-ink-soft">{num(data.token1Amount, 2)}</span>
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="pt-0.5">
        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
          data.inRange
            ? 'bg-accent-green/15 text-accent-green'
            : 'bg-accent-red/15 text-accent-red'
        }`}>
          <span className="w-1.5 h-1.5 rounded-full bg-current"/>
          {data.inRange ? 'IN' : 'OUT'}
        </span>
      </div>

      {/* Price Range */}
      <div>
        <div className="text-xs text-ink-muted mb-1.5">
          <span className="text-ink-soft">${num(data.priceLower, 4)}</span>
          <span className="mx-1 text-gray-400">→</span>
          <span className="text-ink-soft">${num(data.priceUpper, 4)}</span>
        </div>
        {/* Bar */}
        <div className="relative h-1.5 bg-surface rounded-full overflow-visible mb-1">
          <div className="h-full bg-accent-blue/30 rounded-full"
               style={{ width: `${Math.min(parseFloat(data.rangeOccupancy) * 100, 100)}%` }}/>
          <div className={`absolute top-1/2 -translate-y-1/2 w-1.5 h-3 rounded-sm ${data.inRange ? 'bg-accent-green' : 'bg-accent-red'}`}
               style={{ left: `calc(${cursorPct}% - 3px)` }}/>
        </div>
        <div className="text-xs text-gray-500">${num(data.currentPrice, 4)} current</div>
      </div>

      {/* Size */}
      <div className="text-right">
        <span className="font-semibold text-ink">{usd(data.lpValueUsd)}</span>
      </div>

      {/* Fee APR */}
      <div className="text-right">
        <span className="text-accent-blue font-medium">{pct(data.feeAprPct)}</span>
      </div>

      {/* Total APR */}
      <div className="text-right">
        <span className="text-accent-green font-medium">{pct(data.totalAprPct)}</span>
      </div>

      {/* Fees Accrued — total USD of all pending yield */}
      <div className="text-right text-xs">
        {parseFloat(data.totalPendingYieldUsd) > 0 ? (
          <span className="text-accent-green font-medium">{usd(data.totalPendingYieldUsd)}</span>
        ) : (
          <span className="text-gray-400">—</span>
        )}
        {onManage && (
          <button
            onClick={onManage}
            className="mt-1 block ml-auto text-[10px] text-cyan-400 hover:underline"
            title="Manage position"
          >
            manage ↗
          </button>
        )}
      </div>

      {/* Bottom detail row — all pending tokens under "Rewards:" */}
      {(() => {
        type TokenEntry = { symbol: string; mint: string; amount?: string; usd?: string; aprPct?: string }
        const tokens: TokenEntry[] = []
        if (parseFloat(data.pendingFee0) > 0) tokens.push({ symbol: sym0, mint: data.token0Mint, amount: num(data.pendingFee0, 4) })
        if (parseFloat(data.pendingFee1) > 0) tokens.push({ symbol: sym1, mint: data.token1Mint, amount: num(data.pendingFee1, 4) })
        data.rewardTokens?.forEach((r: { symbol: string; mint: string; aprPct: string; pendingAmount: string; pendingUsd: string }) => {
          if (parseFloat(r.pendingAmount) > 0 || parseFloat(r.aprPct) > 0)
            tokens.push({
              symbol: r.symbol, mint: r.mint,
              amount: parseFloat(r.pendingAmount) > 0 ? parseFloat(r.pendingAmount).toFixed(4) : undefined,
              usd:    parseFloat(r.pendingUsd)    > 0 ? usd(r.pendingUsd)                       : undefined,
              aprPct: r.aprPct,
            })
        })
        if (tokens.length === 0) return null
        return (
          <div className="col-span-7 flex items-center flex-wrap gap-x-3 gap-y-0.5 pt-1 pb-0.5 text-xs text-gray-500">
            <span className="text-gray-400">Rewards:</span>
            {tokens.map((t, i) => (
              <span key={t.mint + i} className="flex items-center gap-1">
                <TokenIcon symbol={t.symbol} mint={t.mint} size={11} />
                <span>{t.symbol}</span>
                {t.amount && <span className="text-ink-soft">{t.amount}</span>}
                {t.usd    && <span className="text-accent-green font-medium">{t.usd}</span>}
                {t.aprPct && parseFloat(t.aprPct) > 0 && <span className="text-gray-400">+{pct(t.aprPct)}</span>}
              </span>
            ))}
          </div>
        )
      })()}

    </div>
  )
}
